import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema } from './schemas/common.js';
import { sourceInstanceSchema } from './schemas/domain.js';
const c = initContract();
/**
 * Logging a Telegram account in from the panel.
 *
 * Three steps because Telegram's is three steps: phone, code, and a password
 * only when the account has two-factor enabled. The login lives in the API's
 * memory between calls and expires in minutes — it is never written down, and a
 * restart loses it, which is correct: a half-finished login is not state worth
 * keeping.
 *
 * A warning the panel must show, from docs/telegram.md: Telegram invalidates a
 * login code that appears inside a Telegram chat. This panel runs inside
 * Telegram. If a code stops working, the session string from
 * `pnpm telegram:login` can be pasted into the ordinary source form instead.
 */
export const loginStatusSchema = z.enum([
    'code-required',
    'password-required',
    'completed',
    'expired',
]);
const byId = z.object({ loginId: z.string().min(1) });
export const telegramLoginContract = c.router({
    start: {
        method: 'POST',
        metadata: access('admin'),
        path: '/telegram-logins',
        summary: 'Send a login code to a phone number',
        body: z.object({
            name: z.string().min(1).max(40),
            apiId: z.coerce.number().int().positive(),
            apiHash: z.string().min(8),
            phone: z.string().regex(/^\+?\d{6,20}$/, 'a phone number in international form'),
        }),
        responses: {
            201: z.object({ loginId: z.string(), status: loginStatusSchema }),
            400: apiErrorSchema,
            /** Telegram refused the number or the app credentials. */
            401: apiErrorSchema,
            502: apiErrorSchema,
            503: apiErrorSchema,
        },
    },
    code: {
        method: 'POST',
        metadata: access('admin'),
        path: '/telegram-logins/:loginId/code',
        pathParams: byId,
        body: z.object({ code: z.string().min(3).max(12) }),
        responses: {
            200: z.object({
                status: loginStatusSchema,
                /** Present when the login finished: the account is now a source. */
                instance: sourceInstanceSchema.optional(),
            }),
            401: apiErrorSchema,
            404: apiErrorSchema,
            410: apiErrorSchema,
        },
    },
    password: {
        method: 'POST',
        metadata: access('admin'),
        path: '/telegram-logins/:loginId/password',
        summary: 'The two-factor password, when Telegram asks for one',
        pathParams: byId,
        body: z.object({ password: z.string().min(1) }),
        responses: {
            200: z.object({ status: loginStatusSchema, instance: sourceInstanceSchema.optional() }),
            401: apiErrorSchema,
            404: apiErrorSchema,
            410: apiErrorSchema,
        },
    },
    cancel: {
        method: 'DELETE',
        metadata: access('admin'),
        path: '/telegram-logins/:loginId',
        pathParams: byId,
        body: z.object({}),
        responses: { 200: z.object({ cancelled: z.boolean() }) },
    },
});
//# sourceMappingURL=telegram-login.contract.js.map